// src/utils/sessionBackup.js
// Backup resiliente de sessão via IndexedDB + Cookies
// Protege contra perda de localStorage (Safari ITP, limpeza manual, pressão de storage)

const DB_NAME = 'consultamais_session';
const DB_VERSION = 1;
const STORE_NAME = 'session';

const CRITICAL_KEYS = [
  'patientId', 'activeChannelId', 'appSession', 'fullName', 'patientName',
  // Flag de pagamento: sempre backupear junto com paymentConfirmedFor para
  // que a validação de binding funcione na restauração. Ver restoreSession.
  'paymentConfirmed', 'paymentConfirmedFor', 'paymentConfirmedAt',
  // chatStartedAtFor faz o binding da âncora de expiração ao patientId (mesmo
  // padrão de paymentConfirmedFor) — backupear junto para a validação por dono
  // funcionar após restauração. Ver scopedChatStartedAtMs em ConsultaChat.jsx.
  'chatStartedAt', 'chatStartedAtFor', 'anamneseProntuario', 'anamneseProntuarioFor',
  'prontuarioSent', 'paymentSent', 'patientEmail', 'patientCpf', 'patientPhone', 'patientPhoneDial',
];

const COOKIE_KEYS = ['patientId', 'activeChannelId', 'appSession'];
const COOKIE_MAX_AGE = 604800; // 7 dias

// ── IndexedDB helpers ──

let _dbPromise = null;

// Teto para open/get/put. Sem isso, `indexedDB.open` pendurado no WebView iOS
// deixava o finalize da triagem em "Gerando prontuário..." para sempre
// (Vitória Sousa Silva 16/09/2026). LS/SS já têm o dado; IDB é best-effort.
export const IDB_OP_TIMEOUT_MS = 2500;
let _idbTimeoutMs = IDB_OP_TIMEOUT_MS;

function withTimeout(promise, ms, message) {
  let tid;
  const timeout = new Promise((_, reject) => {
    tid = setTimeout(() => reject(new Error(message || 'idb_timeout')), ms);
  });
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => {
    if (tid) clearTimeout(tid);
  });
}

function openDB() {
  if (!_dbPromise) {
    _dbPromise = new Promise((resolve, reject) => {
      try {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => {
          _dbPromise = null;
          reject(req.error);
        };
      } catch (e) {
        _dbPromise = null;
        reject(e);
      }
    });
  }
  return withTimeout(_dbPromise, _idbTimeoutMs, 'idb_open_timeout').catch((err) => {
    if (String(err?.message || err).includes('idb_open_timeout')) {
      _dbPromise = null;
    }
    throw err;
  });
}

function idbPutRaw(db, key, value) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    } catch (e) { reject(e); }
  });
}

function idbGetRaw(db, key) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) { reject(e); }
  });
}

function idbDeleteRaw(db, key) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    } catch (e) { reject(e); }
  });
}

function idbPut(db, key, value) {
  return withTimeout(idbPutRaw(db, key, value), _idbTimeoutMs, 'idb_put_timeout');
}

function idbGet(db, key) {
  return withTimeout(idbGetRaw(db, key), _idbTimeoutMs, 'idb_get_timeout');
}

function idbDelete(db, key) {
  return withTimeout(idbDeleteRaw(db, key), _idbTimeoutMs, 'idb_delete_timeout');
}

// ── Cookie helpers ──

function setCookie(name, value) {
  try {
    const encoded = encodeURIComponent(value);
    if (encoded.length > 3800) return; // segurança: não exceder limite de cookie
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `_cm_${name}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
  } catch {}
}

function getCookie(name) {
  try {
    const prefix = `_cm_${name}=`;
    const cookies = document.cookie.split(';');
    for (const c of cookies) {
      const trimmed = c.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.slice(prefix.length));
      }
    }
  } catch {}
  return null;
}

function removeCookie(name) {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `_cm_${name}=; path=/; max-age=0; SameSite=Lax${secure}`;
  } catch {}
}

// ── Public API ──

let _backupTimeout = null;

export function scheduleBackup() {
  if (_backupTimeout) clearTimeout(_backupTimeout);
  _backupTimeout = setTimeout(() => {
    _backupTimeout = null;
    backupSession();
  }, 2000);
}

export function cancelScheduledBackup() {
  if (_backupTimeout) {
    clearTimeout(_backupTimeout);
    _backupTimeout = null;
  }
}

export async function backupSession() {
  const snapshot = {};
  for (const key of CRITICAL_KEYS) {
    try {
      const val = localStorage.getItem(key) ?? sessionStorage.getItem(key);
      if (val != null) snapshot[key] = val;
    } catch {}
  }

  if (Object.keys(snapshot).length === 0) return;

  // IndexedDB backup
  try {
    const db = await openDB();
    await idbPut(db, 'session_snapshot', { ...snapshot, _ts: Date.now() });
  } catch {}

  // Cookie backup (apenas chaves mínimas)
  for (const key of COOKIE_KEYS) {
    if (snapshot[key]) setCookie(key, snapshot[key]);
  }
}

export async function restoreSession() {
  const restored = {};

  // Tenta IndexedDB primeiro (mais completo)
  try {
    const db = await openDB();
    const data = await idbGet(db, 'session_snapshot');
    if (data && typeof data === 'object') {
      for (const key of CRITICAL_KEYS) {
        if (data[key] != null) restored[key] = data[key];
      }
    }
  } catch {}

  // Complementa com cookies (pode ter dados mais recentes para chaves mínimas)
  for (const key of COOKIE_KEYS) {
    if (!restored[key]) {
      const val = getCookie(key);
      if (val) restored[key] = val;
    }
  }

  if (Object.keys(restored).length === 0) return null;

  // Defesa contra vazamento do flag de pagamento (caso Palagi):
  // só restaura `paymentConfirmed` se vier com `paymentConfirmedFor` que
  // bate com o `patientId` que também está sendo restaurado. Qualquer
  // descasamento → descarta as 3 chaves de pagamento do restored.
  if (restored.paymentConfirmed === '1') {
    const pid = restored.patientId || null;
    const boundTo = restored.paymentConfirmedFor || null;
    if (!pid || !boundTo || pid !== boundTo) {
      delete restored.paymentConfirmed;
      delete restored.paymentConfirmedFor;
      delete restored.paymentConfirmedAt;
    }
  }

  // Defesa contra vazamento de triagem/canal (caso Arthur Dias 03/08/2026):
  // prontuário só volta do backup se `anamneseProntuarioFor` casar com o
  // patientId restaurado; canal só se o id pertencer a esse patientId.
  // Cookie `_cm_activeChannelId` sem patientId no snapshot também é
  // descartado — senão o PatientLayout redirecionava pro chat órfão.
  {
    const pid = restored.patientId || null;
    const prontFor = restored.anamneseProntuarioFor || null;
    if (restored.anamneseProntuario) {
      if (!pid || !prontFor || prontFor !== pid) {
        delete restored.anamneseProntuario;
        delete restored.anamneseProntuarioFor;
        delete restored.prontuarioSent;
      }
    }
    if (restored.activeChannelId) {
      let owned = false;
      if (pid) {
        const bare = String(restored.activeChannelId).includes(':')
          ? String(restored.activeChannelId).split(':').pop()
          : String(restored.activeChannelId);
        owned = bare === pid || bare.startsWith(`${pid}-`);
      }
      if (!owned) {
        delete restored.activeChannelId;
        delete restored.appSession;
        delete restored.paymentSent;
      }
    }
  }

  // Reescreve em localStorage + sessionStorage
  for (const [key, val] of Object.entries(restored)) {
    try { localStorage.setItem(key, val); } catch {}
    try { sessionStorage.setItem(key, val); } catch {}
  }

  return restored;
}

/**
 * Remove chaves específicas do snapshot de backup (IDB + cookie, quando a
 * chave é cookieável), preservando o resto da sessão.
 *
 * Existe porque limpar só localStorage/sessionStorage não basta: o snapshot do
 * IndexedDB sobrevive e o `restoreSession` / `loadKeyFromBackups` reescreve a
 * chave no boot seguinte. Foi o que mantinha o flag de pagamento vivo depois de
 * um estorno, escondendo a barra "Pagar" de novo.
 */
export async function purgeKeysFromBackup(keys) {
  const list = (Array.isArray(keys) ? keys : [keys]).filter(Boolean);
  if (list.length === 0) return false;

  let purged = false;
  try {
    const db = await openDB();
    const existing = await idbGet(db, 'session_snapshot');
    if (existing && typeof existing === 'object') {
      const snapshot = { ...existing };
      let touched = false;
      for (const key of list) {
        if (key in snapshot) {
          delete snapshot[key];
          touched = true;
        }
      }
      if (touched) {
        snapshot._ts = Date.now();
        await idbPut(db, 'session_snapshot', snapshot);
        purged = true;
      }
    }
  } catch {}

  for (const key of list) {
    if (COOKIE_KEYS.includes(key)) removeCookie(key);
  }

  return purged;
}

export async function clearSessionBackup() {
  try {
    const db = await openDB();
    await idbDelete(db, 'session_snapshot');
  } catch {}

  for (const key of COOKIE_KEYS) {
    removeCookie(key);
  }
}

// Grava IMEDIATAMENTE em localStorage/sessionStorage e tenta IndexedDB
// (merge com snapshot). O IDB tem teto (`IDB_OP_TIMEOUT_MS`): se pendurar,
// LS/SS já valem e o caller pode navegar (caso Vitória 16/09/2026).
// Retorna { ls_ok, ss_ok, idb_ok } para telemetria.
export async function backupNow(key, value) {
  const result = { ls_ok: false, ss_ok: false, idb_ok: false };
  if (!key) return result;
  const v = String(value == null ? '' : value);

  try { localStorage.setItem(key, v); result.ls_ok = true; } catch {}
  try { sessionStorage.setItem(key, v); result.ss_ok = true; } catch {}

  try {
    const db = await openDB();
    let existing = null;
    try { existing = await idbGet(db, 'session_snapshot'); } catch {}
    const snapshot = (existing && typeof existing === 'object') ? { ...existing } : {};
    snapshot[key] = v;
    snapshot._ts = Date.now();
    await idbPut(db, 'session_snapshot', snapshot);
    result.idb_ok = true;
  } catch {}

  if (COOKIE_KEYS.includes(key)) {
    try { setCookie(key, v); } catch {}
  }

  return result;
}

// Lê uma chave a partir das fontes de backup (IDB primeiro; cookie só para
// chaves permitidas). Retorna string ou null. Reidrata localStorage/sessionStorage
// quando algum backup é encontrado para que leituras subsequentes via safeGet
// funcionem normalmente.
export async function loadKeyFromBackups(key) {
  if (!key) return null;
  let value = null;

  try {
    const db = await openDB();
    const snap = await idbGet(db, 'session_snapshot');
    if (snap && typeof snap === 'object' && snap[key] != null) {
      value = String(snap[key]);
    }

    // Defesa contra vazamento: nunca restaura paymentConfirmed isolado.
    // Só vale se `paymentConfirmedFor` bate com `patientId` — seja do
    // snapshot do IDB, seja do storage atual.
    if (key === 'paymentConfirmed' && value === '1' && snap && typeof snap === 'object') {
      const pid =
        snap.patientId ||
        (() => { try { return localStorage.getItem('patientId'); } catch { return null; } })();
      const boundTo = snap.paymentConfirmedFor || null;
      if (!pid || !boundTo || pid !== boundTo) {
        value = null;
      }
    }
  } catch {}

  if (value == null && COOKIE_KEYS.includes(key)) {
    const c = getCookie(key);
    if (c != null) value = c;
  }

  if (value != null) {
    try { localStorage.setItem(key, value); } catch {}
    try { sessionStorage.setItem(key, value); } catch {}
  }

  return value;
}

export { CRITICAL_KEYS, COOKIE_KEYS };

export function __resetSessionBackupIdbForTests() {
  _dbPromise = null;
}

export function __setIdbTimeoutMsForTests(ms) {
  _idbTimeoutMs = ms == null ? IDB_OP_TIMEOUT_MS : Number(ms);
}
